import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeaders } from '../utils/api';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Activity,
  DollarSign,
  BarChart3,
  RefreshCw,
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  Percent,
  Target,
  Shield,
  Zap
} from 'lucide-react';

// Available markets
const MARKETS = [
  { symbol: 'XAUUSD', name: 'Gold', icon: '🥇', type: 'commodity' },
  { symbol: 'EURUSD', name: 'EUR/USD', icon: '💶', type: 'forex' },
  { symbol: 'GBPUSD', name: 'GBP/USD', icon: '💷', type: 'forex' },
  { symbol: 'USDJPY', name: 'USD/JPY', icon: '💴', type: 'forex' },
  { symbol: 'BTCUSD', name: 'Bitcoin', icon: '₿', type: 'crypto' }
];

export default function TradingPanel() {
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [prices, setPrices] = useState({});
  const [selectedMarket, setSelectedMarket] = useState('XAUUSD');
  const [orderForm, setOrderForm] = useState({
    direction: 'buy',
    lot_size: 0.01,
    stop_loss: '',
    take_profit: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch account data
  const fetchAccount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trading/account`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        timeout: 10000
      });
      setAccount(response.data);
    } catch (error) {
      console.error('Failed to fetch account:', error);
    }
  }, []);

  // Fetch positions
  const fetchPositions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trading/positions`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        timeout: 10000
      });
      setPositions(response.data.positions || []);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  }, []);

  // Fetch live prices
  const fetchPrices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trading/prices`, {
        timeout: 10000
      });
      setPrices(response.data.prices || {});
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    }
  }, []);

  // Initial load and refresh
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAccount(), fetchPositions(), fetchPrices()]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      fetchPrices();
      fetchPositions();
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchAccount, fetchPositions, fetchPrices]);

  // Place order
  const placeOrder = async (direction) => {
    if (!account) {
      toast.error('No trading account found');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/trading/order`, {
        market: selectedMarket,
        direction,
        lot_size: parseFloat(orderForm.lot_size),
        stop_loss: orderForm.stop_loss ? parseFloat(orderForm.stop_loss) : null,
        take_profit: orderForm.take_profit ? parseFloat(orderForm.take_profit) : null,
        order_type: 'market'
      }, {
        withCredentials: true,
        headers: getAuthHeaders()
      });

      toast.success(`${direction.toUpperCase()} order executed!`);
      fetchAccount();
      fetchPositions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  // Close position
  const closePosition = async (positionId) => {
    try {
      const response = await axios.post(`${API}/trading/close/${positionId}`, {}, {
        withCredentials: true,
        headers: getAuthHeaders()
      });

      toast.success(`Position closed! P&L: $${response.data.realized_pnl.toFixed(2)}`);
      fetchAccount();
      fetchPositions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close position');
    }
  };

  // Calculate margin for current order
  const calculateMargin = () => {
    const price = prices[selectedMarket]?.price || 0;
    const lotSize = parseFloat(orderForm.lot_size) || 0;
    const leverage = account?.leverage || 100;
    const contractSize = prices[selectedMarket]?.contract_size || 100000;
    
    return ((lotSize * contractSize * price) / leverage).toFixed(2);
  };

  const currentPrice = prices[selectedMarket];
  const currentMarket = MARKETS.find(m => m.symbol === selectedMarket);

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700 bg-gradient-to-r from-zinc-900 to-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl border border-emerald-500/30">
              <Activity className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                TradeForge Trading
                <Badge variant="outline" className="bg-amber-500/20 border-amber-500/50 text-amber-400 text-xs">
                  PAPER TRADING
                </Badge>
              </h3>
              <p className="text-xs text-slate-400">Real market data • Simulated execution</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchAccount(); fetchPositions(); fetchPrices(); }}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Account Summary */}
      <div className="p-4 border-b border-zinc-800 bg-zinc-900/50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-slate-500">Balance</span>
            </div>
            <p className="text-xl font-bold text-white font-mono">${account?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-slate-500">Equity</span>
            </div>
            <p className={`text-xl font-bold font-mono ${account?.equity >= account?.balance ? 'text-emerald-400' : 'text-red-400'}`}>
              ${account?.equity?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-slate-500">Free Margin</span>
            </div>
            <p className="text-xl font-bold text-white font-mono">${account?.free_margin?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-slate-500">Open P&L</span>
            </div>
            <p className={`text-xl font-bold font-mono ${(account?.unrealized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {account?.unrealized_pnl >= 0 ? '+' : ''}${account?.unrealized_pnl?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="trade" className="w-full">
        <TabsList className="w-full bg-zinc-800/50 rounded-none border-b border-zinc-700">
          <TabsTrigger value="trade" className="flex-1 data-[state=active]:bg-zinc-700">
            <Zap className="w-4 h-4 mr-2" />
            Trade
          </TabsTrigger>
          <TabsTrigger value="positions" className="flex-1 data-[state=active]:bg-zinc-700">
            <Activity className="w-4 h-4 mr-2" />
            Positions ({positions.length})
          </TabsTrigger>
        </TabsList>

        {/* Trade Tab */}
        <TabsContent value="trade" className="p-4 space-y-4">
          {/* Market Selector */}
          <div>
            <Label className="text-slate-400 text-xs mb-2 block">Select Market</Label>
            <div className="grid grid-cols-5 gap-2">
              {MARKETS.map((market) => (
                <button
                  key={market.symbol}
                  onClick={() => setSelectedMarket(market.symbol)}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    selectedMarket === market.symbol
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                      : 'bg-zinc-800/50 border-zinc-700 text-slate-400 hover:border-zinc-600'
                  }`}
                >
                  <span className="text-xl block mb-1">{market.icon}</span>
                  <span className="text-xs font-mono">{market.symbol}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Current Price */}
          {currentPrice && (
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs mb-1">{currentMarket?.name}</p>
                  <p className="text-3xl font-bold text-white font-mono">{currentPrice.price?.toFixed(currentPrice.pip_size < 0.01 ? 5 : 2)}</p>
                </div>
                <div className="text-right">
                  <div className="flex gap-4">
                    <div>
                      <p className="text-[10px] text-slate-500">BID</p>
                      <p className="text-sm font-mono text-red-400">{currentPrice.bid?.toFixed(currentPrice.pip_size < 0.01 ? 5 : 2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500">ASK</p>
                      <p className="text-sm font-mono text-emerald-400">{currentPrice.ask?.toFixed(currentPrice.pip_size < 0.01 ? 5 : 2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Order Form */}
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 text-xs mb-2 block">Lot Size</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                value={orderForm.lot_size}
                onChange={(e) => setOrderForm({ ...orderForm, lot_size: e.target.value })}
                className="bg-zinc-800 border-zinc-700 font-mono"
              />
              <p className="text-[10px] text-slate-500 mt-1">Required margin: ${calculateMargin()}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Stop Loss (optional)</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="Price"
                  value={orderForm.stop_loss}
                  onChange={(e) => setOrderForm({ ...orderForm, stop_loss: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 font-mono"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Take Profit (optional)</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="Price"
                  value={orderForm.take_profit}
                  onChange={(e) => setOrderForm({ ...orderForm, take_profit: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 font-mono"
                />
              </div>
            </div>

            {/* Buy/Sell Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => placeOrder('buy')}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 text-lg"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                BUY
              </Button>
              <Button
                onClick={() => placeOrder('sell')}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg"
              >
                <TrendingDown className="w-5 h-5 mr-2" />
                SELL
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Positions Tab */}
        <TabsContent value="positions" className="p-4">
          {positions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No open positions</p>
              <p className="text-xs mt-1">Place a trade to get started</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {positions.map((pos) => (
                <div
                  key={pos.position_id}
                  className={`rounded-xl border p-4 ${
                    pos.unrealized_pnl >= 0
                      ? 'bg-emerald-500/5 border-emerald-500/30'
                      : 'bg-red-500/5 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{MARKETS.find(m => m.symbol === pos.market)?.icon}</span>
                      <span className="font-mono font-bold text-white">{pos.market}</span>
                      <Badge className={pos.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {pos.direction.toUpperCase()}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => closePosition(pos.position_id)}
                      className="text-slate-400 hover:text-white hover:bg-red-500/20"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Close
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs">Lot Size</p>
                      <p className="font-mono text-white">{pos.lot_size}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Entry</p>
                      <p className="font-mono text-white">{pos.entry_price?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Current</p>
                      <p className="font-mono text-white">{pos.current_price?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">P&L</p>
                      <p className={`font-mono font-bold ${pos.unrealized_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pos.unrealized_pnl >= 0 ? '+' : ''}${pos.unrealized_pnl?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  {(pos.stop_loss || pos.take_profit) && (
                    <div className="flex gap-4 mt-2 pt-2 border-t border-white/5">
                      {pos.stop_loss && (
                        <span className="text-xs text-red-400">
                          <Shield className="w-3 h-3 inline mr-1" />
                          SL: {pos.stop_loss}
                        </span>
                      )}
                      {pos.take_profit && (
                        <span className="text-xs text-emerald-400">
                          <Target className="w-3 h-3 inline mr-1" />
                          TP: {pos.take_profit}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        <p className="text-[10px] text-slate-500 text-center">
          Paper trading uses real market data with simulated execution • Account: {account?.account_id}
        </p>
      </div>
    </Card>
  );
}
